import { useContext, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { inject, observer, PropTypes as MobXPropTypes } from 'mobx-react';
import queryString from 'query-string';
import Table from 'react-bootstrap/Table';
import ReconciliationReportsFiltersBar from './components/ReconciliationReportsFiltersBar';
import ReconciliationReportsTableHead from './components/ReconciliationReportsTableHead';
import ReconciliationReportsTableRow from './components/ReconciliationReportsTableRow';
import i18nContext from 'components/i18n-context';
import { DIRECTION, MENU_DIRECTION } from 'components/constants';
import EmptyTableContentPlug from 'components/EmptyTableContentPlug';
import Loader from 'components/Loader';
import Pagination from 'components/Pagination/Pagination';
import { Container } from 'uikit/Container/Container';
import './ReconciliationReportsTable.scss';

const ReconciliationReportsTable = ({ reconciliationReportsStore }) => {
  const i18n = useContext(i18nContext);
  const location = useLocation();
  const navigate = useNavigate();

  const updateFiltersInUrl = (filters, pagination, sortColumn) => {
    const filtersParams = reconciliationReportsStore.prepareFiltersParams();
    const queryParams = {
      ...filtersParams,
      page: pagination.page,
      size: pagination.size,
      sort_column: sortColumn.sort_column,
      sort_direction: sortColumn.sort_direction
    };
    const searchParams = queryString.stringify(queryParams);
    navigate({ search: `?${searchParams}` }, { replace: true });
  };

  const loadFiltersFromUrl = () => {
    const params = queryString.parse(location.search);
    reconciliationReportsStore.setFiltersFromUrl(params);
  };

  useEffect(() => {
    loadFiltersFromUrl();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!reconciliationReportsStore.isInitialized) {
      reconciliationReportsStore.getReconciliationReportsList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reconciliationReportsStore.isInitialized]);

  useEffect(() => {
    if (reconciliationReportsStore.isInitialized) {
      reconciliationReportsStore.resetReconciliationReportsStore();
      loadFiltersFromUrl();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  useEffect(() => {
    updateFiltersInUrl(
      reconciliationReportsStore.filters,
      reconciliationReportsStore.pagination,
      reconciliationReportsStore.sortColumn
    );
    if (reconciliationReportsStore.isInitialized) {
      reconciliationReportsStore.getReconciliationReportsList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    reconciliationReportsStore.filters.reportDate,
    reconciliationReportsStore.filters.transferProvider,
    reconciliationReportsStore.filters.currency,
    reconciliationReportsStore.pagination.size,
    reconciliationReportsStore.pagination.page,
    reconciliationReportsStore.sortColumn.sort_column,
    reconciliationReportsStore.sortColumn.sort_direction
  ]);

  const handleOnSortTable = (newSortBy) => {
    reconciliationReportsStore.setSortData({
      sortBy: newSortBy,
      direction:
        reconciliationReportsStore.sortColumn.sort_column !== newSortBy
          ? DIRECTION.ASC
          : reconciliationReportsStore.sortColumn.sort_direction === DIRECTION.ASC
            ? DIRECTION.DESC
            : DIRECTION.ASC
    });
  };

  if (!reconciliationReportsStore.isInitialized) {
    return <Loader className={'application-loader'} />;
  }

  return (
    <Container
      className={'reconciliation-reports-container'}
      header={i18n.getMessage('container.reconciliationReports')}
    >
      <ReconciliationReportsFiltersBar
        isGeneratingReconciliationReport={reconciliationReportsStore.isGeneratingReconciliationReport}
        isInitialized={reconciliationReportsStore.isInitialized}
        filters={reconciliationReportsStore.filters}
        handleSetFilters={reconciliationReportsStore.setFilter}
        handleGenerateReconciliationReportCsv={reconciliationReportsStore.generateReconciliationReportCsv}
      />

      {reconciliationReportsStore.reconciliationReportsList?.length === 0 ? (
        <EmptyTableContentPlug
          headerText={i18n.getMessage('reconciliationReports.table.emptyPlug.header')}
          descriptionText={i18n.getMessage('reconciliationReports.table.emptyPlug.description')}
        />
      ) : (
        <>
          <Pagination
            isShowPaginationSize={true}
            paginationSize={reconciliationReportsStore.pagination.size}
            handleChangePaginationSize={reconciliationReportsStore.setPageSize}
            isLoading={reconciliationReportsStore.isLoading}
            currentPage={reconciliationReportsStore.pagination.page}
            numberOfPages={reconciliationReportsStore.pagination.totalPages}
            setCurrentPage={reconciliationReportsStore.setPageNumber}
          />

          <Table responsive>
            <thead>
              <ReconciliationReportsTableHead
                handleOnSortTable={handleOnSortTable}
                sortBy={reconciliationReportsStore.sortColumn.sort_column}
                direction={reconciliationReportsStore.sortColumn.sort_direction}
              />
            </thead>
            <tbody>
              {reconciliationReportsStore.reconciliationReportsList?.map((reconciliationReport, index) => (
                <ReconciliationReportsTableRow
                  key={index}
                  type={'list'}
                  isLoading={reconciliationReportsStore.isLoading}
                  reconciliationReportData={reconciliationReport}
                />
              ))}
            </tbody>
          </Table>

          <Pagination
            isShowPaginationSize={true}
            paginationSize={reconciliationReportsStore.pagination.size}
            handleChangePaginationSize={reconciliationReportsStore.setPageSize}
            isLoading={reconciliationReportsStore.isLoading}
            currentPage={reconciliationReportsStore.pagination.page}
            numberOfPages={reconciliationReportsStore.pagination.totalPages}
            setCurrentPage={reconciliationReportsStore.setPageNumber}
            menuDirection={MENU_DIRECTION.UP}
          />
        </>
      )}
    </Container>
  );
};

ReconciliationReportsTable.propTypes = {
  reconciliationReportsStore: MobXPropTypes.observableObject
};

export default inject((stores) => ({
  reconciliationReportsStore: stores.reconciliationReportsStore
}))(observer(ReconciliationReportsTable));
